import React from "react";
import { Button, If, Modal, Row, TruncatedText } from "@hex-insights/core";
import {
	FormState,
	FormTooltipButton,
	FormTooltipButtonButtonProps,
	FormTooltipButtonTooltipProps,
} from "@hex-insights/forms";
import { Icon } from "../../../../Components/Icon";
import { useSharingRecordUsersPreviewQuery, useUserCountQuery, useUserGroupNamesQuery } from "../../../../Utilities";
import { SharingRecordFormat } from "../../../Format";
import { ControlledUsersListModal } from "../../User";
import { SharingTooltip, SimpleSharingModal, SimpleSharingModalProps } from "./Components";
import { ControlledSharedUsersPreviewListModalController } from "./Components/Preview";
import { SharingMenuFormValues } from "./utils";
import "./styles.css";

export { type SharingMenuFormValues } from "./utils";

export type NestedCreateSharingMenuProps = {
	formState: FormState<SharingMenuFormValues>;
};

export function NestedCreateSharingMenu({ formState }: NestedCreateSharingMenuProps) {
	return (
		<FormTooltipButton formState={formState}>
			{NestedCreateSharingMenuTooltip}
			{NestedCreateSharingMenuButton}
		</FormTooltipButton>
	);
}

function NestedCreateSharingMenuTooltip({ formState, isOpen }: FormTooltipButtonTooltipProps<SharingMenuFormValues>) {
	return <SharingTooltip formState={formState} isOpen={isOpen} />;
}

function NestedCreateSharingMenuButton({
	formState,
	onClick,
	disabled,
}: FormTooltipButtonButtonProps<SharingMenuFormValues>) {
	const { loading, data } = useSharingRecordUsersPreviewQuery({
		variables: { sharingRecord: formState.formValues, pagination: { first: 0 } },
	});
	const { loading: loadingUserGroups, data: userGroupData } = useUserGroupNamesQuery({
		variables: { filters: { idIn: formState.formValues.userGroupIDs } },
		skip: formState.formValues.userGroupIDs.length === 0,
	});
	const sharingRecord = {
		isPublic: formState.formValues.isPublic,
		userGroups: userGroupData?.userGroupConnection.edges.map((edge) => edge?.node) ?? [],
	};
	const totalSharedUsers = data?.sharedUserConnection.totalCount ?? 0;
	const baseText = SharingRecordFormat.preview(sharingRecord, totalSharedUsers);
	const showViewButton = baseText !== "Private" && baseText !== "Everyone";
	const isPrivate = baseText === "Private";
	const text = baseText === "Private" ? "Only You" : baseText;

	const isLoading = loading || loadingUserGroups;

	return (
		<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
			<Button variant="tertiary" onClick={onClick} disabled={disabled}>
				<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
					<Icon.Users size={16} />
					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
						Share with:
						<TruncatedText
							style={{ maxWidth: "15rem", color: isPrivate && !isLoading ? "var(--theme-colors__orange)" : undefined }}
						>
							{isLoading ? "Loading..." : text}
						</TruncatedText>
					</Row>
				</Row>
			</Button>

			<If condition={showViewButton}>
				<ControlledSharedUsersPreviewListModalController variant="link" size="small" formValues={formState.formValues}>
					View All
				</ControlledSharedUsersPreviewListModalController>
			</If>
		</Row>
	);
}

export type EditSharingMenuProps = Pick<SimpleSharingModalProps, "sharingRecord">;

export function EditSharingMenu({ sharingRecord }: EditSharingMenuProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const { loading, data } = useUserCountQuery({
		variables: { filters: { sharedSharingRecords: [{ idEQ: sharingRecord.id }] } },
	});
	const totalSharedUsers = data?.userConnection.totalCount ?? 0;
	const text = SharingRecordFormat.preview(sharingRecord, totalSharedUsers);
	const showViewButton = text !== "Private" && text !== "Everyone";

	const { isModalOpen: isUsersModalOpen, openModal: openUsersModal, closeModal: closeUsersModal } = Modal.useToggle();
	const userFilters = React.useMemo(
		() => ({
			sharedSharingRecords: [
				{
					idEQ: sharingRecord.id,
				},
			],
		}),
		[sharingRecord.id],
	);

	return (
		<React.Fragment>
			<Row justify="spaced-start" align="center">
				<Button variant="tertiary" onClick={openModal}>
					<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
						<Icon.Users size={16} />
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							Share with: <TruncatedText style={{ maxWidth: "15rem" }}>{loading ? "Loading..." : text}</TruncatedText>
						</Row>
					</Row>
				</Button>

				<If condition={showViewButton}>
					<Button variant="link" size="small" onClick={openUsersModal}>
						View All
					</Button>
				</If>
			</Row>

			<Modal.If condition={isModalOpen}>
				<SimpleSharingModal sharingRecord={sharingRecord} onClose={closeModal} />
			</Modal.If>

			<Modal.If condition={isUsersModalOpen}>
				<ControlledUsersListModal heading="Shared With" filters={userFilters} onClose={closeUsersModal} />
			</Modal.If>
		</React.Fragment>
	);
}
